
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { SEO } from "@/components/common/SEO";
import { Header } from "@/components/learn-more/Header";
import { Footer } from "@/components/learn-more/Footer";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { BlogPostEditor } from "@/components/cms/BlogPostEditor";
import { BlogPostList } from "@/components/cms/BlogPostList";
import { blogPosts } from "@/data/blogPosts";
import { BlogPost } from "@/types/Blog";
import { Plus, FileText, Settings } from "lucide-react";
import { toast } from "@/hooks/use-toast";

export default function BlogCMS() {
  const [currentView, setCurrentView] = useState<"list" | "editor">("list");
  const [editingPost, setEditingPost] = useState<BlogPost | null>(null);
  const navigate = useNavigate();

  const handleCreateNew = () => {
    setEditingPost(null);
    setCurrentView("editor");
  };

  const handleEditPost = (post: BlogPost) => {
    setEditingPost(post);
    setCurrentView("editor");
  };

  const handleSavePost = (postData: Partial<BlogPost>) => {
    // In a real implementation, this would save to your backend/CMS
    toast({
      title: "Post saved!",
      description: "Your blog post has been saved. Copy the generated code to add it to your blog.",
      duration: 5000,
    });
    setCurrentView("list");
  };

  const handleBackToList = () => {
    setCurrentView("list");
    setEditingPost(null);
  };

  return (
    <div className="min-h-screen flex flex-col">
      <SEO 
        title="Blog CMS | Hour Tracker"
        description="Content management system for Hour Tracker blog"
        keywords="blog cms, content management"
      />
      
      <Header />
      
      <main className="flex-1 py-12 bg-gradient-to-b from-gray-50 to-white">
        <div className="container px-4 sm:px-6 max-w-6xl mx-auto">
          <div className="mb-8">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-3xl font-bold mb-2">Blog Content Management</h1>
                <p className="text-muted-foreground">Create and manage your blog posts</p>
              </div>
              
              {currentView === "list" && (
                <Button onClick={handleCreateNew} className="gap-2">
                  <Plus className="h-4 w-4" />
                  New Post
                </Button>
              )}
            </div>
          </div>

          {currentView === "list" ? (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                <Card>
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium">Total Posts</CardTitle>
                    <FileText className="h-4 w-4 text-muted-foreground" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">{blogPosts.length}</div>
                  </CardContent>
                </Card>
                
                <Card>
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium">Categories</CardTitle>
                    <Settings className="h-4 w-4 text-muted-foreground" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">
                      {new Set(blogPosts.map(post => post.category)).size}
                    </div>
                  </CardContent>
                </Card>
                
                <Card>
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium">Last Updated</CardTitle>
                    <FileText className="h-4 w-4 text-muted-foreground" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-sm">
                      {new Date(blogPosts[0]?.publishedAt || Date.now()).toLocaleDateString()}
                    </div>
                  </CardContent>
                </Card>
              </div>

              <BlogPostList 
                posts={blogPosts} 
                onEditPost={handleEditPost}
              />
            </div>
          ) : (
            <BlogPostEditor 
              post={editingPost}
              onSave={handleSavePost}
              onCancel={handleBackToList}
            />
          )}
        </div>
      </main>
      
      <Footer />
    </div>
  );
}
